import './style.scss';
import { BaseSyntheticEvent, FC, useEffect, useState } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BillingInfoForm } from '@global/components/BillingInfoForm/BillingInfoForm';

import { useAppSelector } from '@src/global/store/hooks';
import { subscriptionSelector } from '@src/global/store/slices/subscription/selectors';

import {
  BillingFormType,
  BillingInfoFormValuesType,
  EMPTY_BILLING_FORM_VALUES,
} from '@src/global/components/BillingInfoForm/types';

import { IBillingInfo } from '@global/types/types';

import { cn } from '@global/utils/styling';
import { SiteConfig } from '@src/global/utils/SiteConfig';

interface ChangeBillingInfoFormProps {
  isShow: boolean;
  setIsShow: (value: boolean) => void;
  buttonId: string;
  billingInfo: IBillingInfo;
  methods: UseFormReturn<BillingInfoFormValuesType>;
  onSubmit?: (e?: BaseSyntheticEvent) => Promise<void>;
  buttonText: string;
  isCartButtonLoading?: boolean;
  setIsCartButtonLoading?: (value: boolean) => void;
  withPaymentForm?: boolean;
  formType: BillingFormType;
  setPaymentErr?: React.Dispatch<React.SetStateAction<string | null>>;
}

export const ChangeBillingInfoForm: FC<ChangeBillingInfoFormProps> = ({
  isShow,
  setIsShow,
  buttonId,
  billingInfo,
  methods,
  onSubmit,
  buttonText,
  isCartButtonLoading,
  setIsCartButtonLoading,
  withPaymentForm,
  formType,
  setPaymentErr,
}) => {
  const { isClicked, isButtonLoading } = useAppSelector(subscriptionSelector);
  const { t } = useTranslation();
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    if (isShow && billingInfo) {
      methods.setValue('country', billingInfo.country?.iso2Code);
      methods.setValue('CCname', billingInfo.name);

      methods.setValue('address1', billingInfo.addressOne);
      methods.setValue('address2', billingInfo.addressTwo);

      methods.setValue('city', billingInfo.city);
      methods.setValue('zip', billingInfo.zip);
      methods.setValue('state', billingInfo.state);

      methods.setValue('phone', billingInfo.tel);
      methods.trigger();
    }

    if (!billingInfo) {
      methods.reset(EMPTY_BILLING_FORM_VALUES);
    }
  }, [isShow, billingInfo]);

  useEffect(() => {
    setDisabled(!methods.formState.isValid || isClicked);
  }, [methods.formState.isValid, isClicked]);

  return (
    <div
      className={cn('change-billing-form', {
        'change-billing-form__hidden-form':
          billingInfo && formType === BillingFormType.ChangePaymentForm,
      })}
      hidden={!isShow}
    >
      <FormProvider {...methods}>
        <BillingInfoForm
          onSubmit={onSubmit}
          buttonId={buttonId}
          buttonText={buttonText}
          isButtonLoading={isButtonLoading}
          buttonCancelText={t('subDetails.sub.billing.billingEditCta.cancel')}
          buttonCancelAction={() => setIsShow(false)}
          isLoading={false}
          disabledSubmit={disabled}
          billingInfo={billingInfo}
          isCartButtonLoading={isCartButtonLoading}
          setIsCartButtonLoading={setIsCartButtonLoading}
          enabledAddressDoctor={!billingInfo}
          setPaymentErr={setPaymentErr}
          formType={formType}
          withPaymentForm={withPaymentForm}
          isCountrySelectDisabled={['en-gb', 'de-de'].includes(
            SiteConfig.getConfig().locale
          )}
        />
      </FormProvider>
    </div>
  );
};
